<template>
  <div class="appPackageListField">
    <div class="appPackageListField__grid">
      <div
        v-for="(item, index) in packageList"
        :key="packageRowKeys[index]"
        class="appPackageListField__cell"
      >
        <el-input
          :value="packageList[index]"
          class="appPackageListField__input"
          placeholder="例如 com.example.app"
          :disabled="disabled"
          @input="(value) => updatePackage(index, value)"
        />
        <button
          v-if="!disabled"
          type="button"
          class="appPackageListField__removeBtn"
          aria-label="移除包名"
          @click="removePackage(index)"
        >
          <i class="el-icon-close" aria-hidden="true" />
        </button>
      </div>
    </div>
    <button
      v-if="!disabled && !hideAddButton"
      type="button"
      class="appPackageListField__addBtn"
      @click="addPackage"
    >
      <i class="el-icon-plus" aria-hidden="true" />
      添加包名
    </button>
  </div>
</template>

<script>
export default {
  name: 'AppPackageListField',
  props: {
    packageList: { type: Array, required: true },
    disabled: { type: Boolean, default: false },
    /** 由外层 label 行提供添加按钮时设为 true */
    hideAddButton: { type: Boolean, default: false },
  },
  data() {
    return {
      // 与包名条目绑定的稳定 key（按内容重匹配，不按「只在末尾补长度」）
      packageRowKeys: [],
      /** 上一轮 packageList 快照，用于插入/重排时找回原 key */
      prevPackageSnapshot: [],
    }
  },
  watch: {
    packageList: {
      immediate: true,
      handler(list) {
        this.syncPackageRowKeys(Array.isArray(list) ? list : [])
      },
    },
  },
  methods: {
    createPackageRowKey() {
      return `pkg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    },
    /**
     * 按「相同值优先复用旧 key」重排，避免父级在中间插入/重排时 key 与元素错位。
     * 空串等重复值按出现顺序贪心匹配，避免两个 '' 抢同一个 key。
     */
    syncPackageRowKeys(next) {
      const prev = this.prevPackageSnapshot
      const prevKeys = this.packageRowKeys
      const usedPrev = new Set()
      const nextKeys = next.map((value) => {
        for (let j = 0; j < prev.length; j++) {
          if (usedPrev.has(j)) continue
          if (prev[j] === value && prevKeys[j]) {
            usedPrev.add(j)
            return prevKeys[j]
          }
        }
        return this.createPackageRowKey()
      })
      this.packageRowKeys = nextKeys
      this.prevPackageSnapshot = next.slice()
    },
    emitList(next) {
      this.$emit('update:packageList', next)
      this.$emit('change', next)
    },
    addPackage() {
      this.emitList([...(this.packageList || []), ''])
    },
    removePackage(index) {
      const next = (this.packageList || []).slice()
      next.splice(index, 1)
      this.emitList(next)
    },
    updatePackage(index, value) {
      const next = (this.packageList || []).slice()
      next[index] = value
      this.emitList(next)
    },
  },
}
</script>

<style scoped>
.appPackageListField {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/** 两列网格：无包名时也保留一行高度的占位，避免点「添加包名」后布局跳动 */
.appPackageListField__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  min-height: 40px;
}

.appPackageListField__cell {
  position: relative;
  min-width: 0;
}

.appPackageListField__input {
  width: 100%;
}

.appPackageListField__input >>> .el-input__inner {
  padding-right: 34px;
}

.appPackageListField__removeBtn {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--link-ink-muted);
  cursor: pointer;
}

.appPackageListField__removeBtn:hover {
  color: var(--link-status-danger-ink, #b91c1c);
  background: rgba(254, 226, 226, 0.55);
}

.appPackageListField__addBtn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 12px;
  border: 1px dashed var(--link-line-strong, #e5dfd6);
  border-radius: 8px;
  background: var(--link-field-bg, #f3f0ea);
  font-family: var(--link-font-family);
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
  cursor: pointer;
}

.appPackageListField__addBtn:hover {
  border-color: var(--link-accent);
  color: var(--link-accent);
  background: var(--link-accent-bg, #f0f7ff);
}

@media (max-width: 720px) {
  .appPackageListField__grid {
    grid-template-columns: 1fr;
  }
}
</style>
